import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next'; // 1. ייבוא
import { Clock, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { he, enUS } from 'date-fns/locale'; // 2. ייבוא לוקאל גם לאנגלית
import { Badge } from '../ui/badge.jsx';

// אובייקט זה נשאר כי הוא קשור לעיצוב ולא לתוכן
const categoryColors = {
    computer_science: 'bg-[#b8c1ec] text-[#232946]',
    judaism_philosophy: 'bg-[#eebbc3] text-[#232946]',
    art: 'bg-[#fffffe] text-[#232946]',
    neuroscience: 'bg-[#d4d8f0] text-[#232946]',
    psychology: 'bg-[#ebccf4] text-[#232946]', // הוספתי את הקטגוריה החסרה
    other: 'bg-[#8890b3] text-white'
};

// 3. הסרנו את האובייקט categoryNames. התרגום יגיע מקבצי ה-JSON.

export default function FeaturedPost({ post, small = true }) {
    const { t, i18n } = useTranslation(); // 4. הפעלת ה-hook

    // 5. בחירה דינאמית של לוקאל התאריך
    const dateLocale = i18n.language.startsWith('he') ? he : enUS;

    if (small) {
        return (
            <Link
                to={`/post/${post.id}`}
                className="flex items-center gap-4 p-2 rounded-xl bg-[#f3f4f6] hover:bg-[#eebbc3]/30 transition group"
                style={{ minHeight: 88 }}
            >
                <img
                    src={post.imageUrl || 'https://images.unsplash.com/photo-1516414447565-b14be0adf13e?auto=format&fit=crop&q=80'}
                    alt={post.title}
                    className="rounded-xl w-20 h-20 object-cover border"
                />
                <div className="flex-1 min-w-0">
                    <Badge className={`${categoryColors[post.category]} text-xs px-2 py-0.5 mb-1`}>
                        {/* 6. תרגום שם הקטגוריה */}
                        {t(`categories.${post.category}`)}
                    </Badge>
                    <h3 className="font-bold text-[#232946] text-base mb-1 group-hover:text-[#eebbc3] transition">
                        {post.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-[#8890b3]">
                        <Clock className="w-3 h-3" />
                        <span>
                            {/* 7. שימוש בלוקאל הדינמי ותרגום הטקסט החלופי */}
                            {post.createdAt && !isNaN(new Date(post.createdAt))
                                ? format(new Date(post.createdAt), 'dd MMMM yyyy', { locale: dateLocale })
                                : t('post.no_date')}
                        </span>
                        <Eye className="w-3 h-3 ml-2" />
                        {/* 8. תרגום המילה "צפיות" */}
                        <span>{post.views}</span>
                    </div>
                </div>
            </Link>
        );
    }

    // גרסה גדולה
    return (
        <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative group"
        >
            <Link
                to={`/post/${post.id}`}
                className="block overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500"
            >
                <div className="relative aspect-[21/9] overflow-hidden">
                    <img
                        src={post.imageUrl || 'https://images.unsplash.com/photo-1516414447565-b14be0adf13e?auto=format&fit=crop&q=80'}
                        alt={post.title}
                        className="object-cover w-full h-full transform transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#232946]/90 via-[#232946]/50 to-transparent" />
                </div>
                <div className="absolute bottom-0 right-0 left-0 p-8 md:p-12">
                    <div className="flex flex-wrap items-center gap-4 mb-6">
                        <Badge className={`${categoryColors[post.category]} text-sm px-4 py-1`}>
                            {t(`categories.${post.category}`)}
                        </Badge>
                        <div className="flex items-center gap-2 text-[#b8c1ec]">
                            <Clock className="w-4 h-4" />
                            <time dateTime={post.createdAt} className="text-sm">
                                {post.createdAt && !isNaN(new Date(post.createdAt))
                                    ? format(new Date(post.createdAt), 'dd MMMM yyyy', { locale: dateLocale })
                                    : t('post.no_date')}
                            </time>
                        </div>
                        <div className="flex items-center gap-2 text-[#b8c1ec]">
                            <Eye className="w-4 h-4" />
                            <span className="text-sm">{post.views} {t('post.views')}</span>
                        </div>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white group-hover:text-[#eebbc3] transition-colors">
                        {post.title}
                    </h2>
                    <p className="text-[#b8c1ec] text-lg line-clamp-2 max-w-3xl">
                        {post.excerpt}
                    </p>
                </div>
            </Link>
        </motion.article>
    );
}